import { action, computed, observable, runInAction } from 'mobx';
import React from 'react';
import { GridColDef } from '../../components/Grid/Grid';
import AbstractStore from '../../store/AbstractStore';
import { PRACTICE_PATH } from '../../store/constants';
import { parseCsvFile } from '../../utils/csv-utils';
import {
  AddVersionActionsRenderer,
  addVersionRowCellRenderer,
  ModalUpgradeVersionsCustomFooter,
  SapUpgradeVersion,
} from './updateUpgradeVersionsRenderers';

interface Loaders {
  addVersion?: boolean;
  validateVersions?: boolean;
  updateVersions?: boolean;
}

interface CsvRecord {
  'SAP ID': string;
  'CS Version': string;
}

interface SapUpgradeVersionEditObject {
  item: SapUpgradeVersion;
  errors: Partial<Record<keyof SapUpgradeVersion, string>>;
}

const emptyVersion = (): SapUpgradeVersion => ({ sapId: '', csVersion: '' });

export default class UpdateUpgradeVersionsStore extends AbstractStore {
  @observable loaders: Loaders = {};

  @observable editObjects: SapUpgradeVersionEditObject[] = [];

  @observable validated = false;

  @computed
  public get sapUpgradeVersionsValid(): boolean {
    if (!this.validated || this.editObjects.length === 0) return false;
    for (const sapUpgradeVersionEditObject of this.editObjects) {
      if (Object.keys(sapUpgradeVersionEditObject.errors).length > 0) return false;
    }
    return true;
  }

  public get addUsersColDef(): GridColDef<SapUpgradeVersion>[] {
    return [
      { headerName: 'SAP ID', field: 'sapId', renderer: addVersionRowCellRenderer('sapId') },
      {
        headerName: 'CS Version',
        field: 'csVersion',
        renderer: addVersionRowCellRenderer('csVersion'),
      },
      { headerName: 'Actions', field: 'actions', renderer: AddVersionActionsRenderer },
    ];
  }

  @action
  public init(): void {
    this.root.globalStore.globalModalProps.customFooter = React.createElement(
      ModalUpgradeVersionsCustomFooter,
    );
    this.loaders = {};
    this.editObjects = [];
    this.addRow();
    this.validated = false;
  }

  @action
  public async processSapVersionsFile(file: File): Promise<void> {
    runInAction(() => (this.loaders.addVersion = true));
    const editObjects: SapUpgradeVersionEditObject[] = [];
    try {
      const rows = await parseCsvFile<CsvRecord>(file, ['SAP ID', 'CS Version']);
      rows.forEach(row => {
        editObjects.push({
          item: {
            sapId: (String(row['SAP ID']) || '').trim(),
            csVersion: (String(row['CS Version']) || '').trim(),
          },
          errors: {},
        });
      });
      runInAction(() => {
        this.editObjects = editObjects;
        this.validated = false;
      });
    } catch (e: any) {
      alert(`Error parsing CSV file: ${e}`);
    }
    runInAction(() => (this.loaders.addVersion = false));
  }

  @action
  public addRow(): void {
    this.editObjects.push({ item: emptyVersion(), errors: {} });
    this.validated = false;
  }

  @action
  public deleteRow(row: any): void {
    const index = this.editObjects.map(o => o.item).findIndex(r => r === row);
    this.editObjects.splice(index, 1);
  }

  @action
  public async validateSapUpgradeVersions(): Promise<void> {
    runInAction(() => (this.loaders.validateVersions = true));
    try {
      const errors = await this.root.apiStore.backendPost<
        Partial<Record<keyof SapUpgradeVersion, string>>
      >(PRACTICE_PATH, {
        action: 'VALIDATE_SAP_CS_VERSIONS',
        sapUpgradeVersions: this.editObjects.map(o => o.item),
      });
      runInAction(() => {
        this.editObjects.forEach((obj, index) => {
          obj.errors = errors[index];
        });
        this.validated = true;
      });
    } catch (e: any) {
      alert(`Validation error: ${e.message}`);
    }
    runInAction(() => {
      this.loaders.validateVersions = false;
    });
  }

  @action
  public async updateSapUpgradeVersions(): Promise<void> {
    runInAction(() => {
      this.loaders.updateVersions = true;
      this.root.globalStore.globalModalProps.okDisabled = true;
    });
    try {
      await this.root.apiStore.backendPost(PRACTICE_PATH, {
        action: 'UPDATE_SAP_CS_VERSIONS',
        sapUpgradeVersions: this.editObjects.map(o => o.item),
      });
      runInAction(() => {
        this.loaders.updateVersions = false;
        this.root.globalStore.globalModalProps.show = false;
        void this.root.upgradeManagementStore.refresh();
      });
    } catch (e: any) {
      alert(`Upgrade Version update error: ${e.message}`);
    }
    runInAction(() => {
      this.loaders.updateVersions = false;
    });
  }
}
